import { useTranslation } from 'react-i18next'
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FooterProps {
  className?: string
}

export function Footer({ className }: FooterProps) {
  const { t } = useTranslation('common')

  const navigationLinks = [
    { key: 'home' as const, label: t('nav.home') },
    { key: 'company' as const, label: t('nav.company') },
    { key: 'products' as const, label: t('nav.products') },
    { key: 'contacts' as const, label: t('nav.contacts') },
    { key: 'sustainability' as const, label: t('nav.sustainability') },
  ]

  const legalLinks = [
    { key: 'privacy' as const, label: t('footer.privacy') },
    { key: 'cookies' as const, label: t('footer.cookiePolicy') },
  ]

  return (
    <footer className={cn('bg-neutral-900 text-white', className)}>
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <LocalizedLink
              to="home"
              className="inline-block text-2xl font-heading font-bold mb-4 hover:text-brand transition-colors"
            >
              Bonifanti
            </LocalizedLink>
            <p className="text-neutral-400 text-sm mb-6">
              {t('footer.tagline')}
            </p>
            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/Bonifanti1932/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-brand hover:text-neutral-900 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/bonifanti_panettone/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-brand hover:text-neutral-900 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              {t('footer.navigation')}
            </h3>
            <ul className="space-y-2">
              {navigationLinks.map((link) => (
                <li key={link.key}>
                  <LocalizedLink
                    to={link.key}
                    className="text-neutral-400 hover:text-brand transition-colors"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacts Column */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              {t('footer.contacts')}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                <span className="text-neutral-400 text-sm">
                  {t('contact.address')}<br />
                  {t('contact.city')}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-brand flex-shrink-0" />
                <a
                  href={`tel:${t('contact.phone')}`}
                  className="text-neutral-400 hover:text-brand transition-colors text-sm"
                >
                  {t('contact.phone')}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-brand flex-shrink-0" />
                <a
                  href={`mailto:${t('contact.email')}`}
                  className="text-neutral-400 hover:text-brand transition-colors text-sm"
                >
                  {t('contact.email')}
                </a>
              </li>
            </ul>
          </div>

          {/* Certifications Column */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              {t('footer.certifications')}
            </h3>
            <div className="space-y-4">
              {/* BRC Certification */}
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white rounded flex items-center justify-center">
                  <span className="text-neutral-900 font-heading font-bold text-xs">BRC</span>
                </div>
                <span className="text-neutral-400 text-sm">BRC Food Certified</span>
              </div>
              {/* IEI Certification placeholder */}
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white rounded flex items-center justify-center">
                  <span className="text-neutral-900 font-heading font-bold text-xs">IEI</span>
                </div>
                <span className="text-neutral-400 text-sm">IEI Certified</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            {/* Legal Links */}
            <div className="flex items-center space-x-6">
              {legalLinks.map((link) => (
                <LocalizedLink
                  key={link.key}
                  to={link.key}
                  className="text-neutral-500 hover:text-brand text-sm transition-colors"
                >
                  {link.label}
                </LocalizedLink>
              ))}
            </div>

            {/* Copyright */}
            <p className="text-neutral-500 text-sm text-center md:text-right">
              © {new Date().getFullYear()} Bonifanti. {t('footer.allRightsReserved')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
